import type { ReactNode } from 'react';
import type { MouseEvent } from 'react';
import { Chat } from '@mui/icons-material';
import { UserRole } from '~/shared/types/connect';
import {
  Icon16,
  ExpertChip,
  ExpertChipAvatar,
  ExpertChipName,
  ExpertChipStatus,
  PartyChip,
  MessageCountBadge,
} from '~/modules/matters/MatterCard.styles';
import { CardActionButton } from '~/modules/matters/MatterCard.styles';
import {
  ReceiptRow,
  ReceiptLabel,
  ReceiptValue,
  RetainerCardReceiptBox,
  RetainerCardHeaderGroup,
  RetainerCardTitle,
  RetainerCardDescription,
  StyledRetainerCard,
  RetainerCardFooterSection,
  RetainerCardFooterRow,
  RetainerCardFooterPartyBlock,
  RetainerCardFooterActions,
  RetainerStatusChip,
  type RetainerStatusChipStatus,
  MarkAsClosedLink,
  RetainerCardActionsRow,
} from '~/modules/matters/RetainerCard.styles';

export interface RetainerCardReceiptLine {
  id?: string;
  label: string;
  value: string;
  /** When 'error', the value is shown in theme red (e.g. for "Awaiting attorney approval") */
  /** When 'success', the value is shown in theme green (e.g. for "Active" status) */
  valueColor?: 'error' | 'success';
}

export interface RetainerCardExpert {
  id: string;
  name: string | null;
  avatarUrl?: string | null;
  role?: UserRole;
}

interface Props {
  title: string;
  subtitle?: string;
  receiptLines: RetainerCardReceiptLine[];
  expert?: RetainerCardExpert | null;
  footerParty?: RetainerCardExpert | null;
  expertStatus?: {
    label: string;
    status: RetainerStatusChipStatus;
  };
  primaryActions?: ReactNode;
  onMessageClick?: () => void;
  onMarkAsClosed?: () => void;
  hideFooter?: boolean;
  /** Unread message count from the other party; when > 0, a red dot is shown on the Message button. */
  unreadCount?: number;
}

function getExpertChipStatusColor(
  status: RetainerStatusChipStatus,
): 'red' | 'success' | 'default' {
  if (status === 'Pending') {
    return 'red';
  }
  if (status === 'Active') {
    return 'success';
  }
  return 'default';
}

interface RetainerCardFooterAvatarProps {
  person: RetainerCardExpert;
  nameFallback: string;
}

const RetainerCardFooterAvatar = ({
  person,
  nameFallback,
}: RetainerCardFooterAvatarProps) => {
  return (
    <ExpertChipAvatar
      avatarUrl={person.avatarUrl}
      initialsLabel={person.name ?? nameFallback}
      alt={person.name ?? ''}
    />
  );
};

const RetainerCard = ({
  title,
  subtitle,
  receiptLines,
  expert,
  footerParty,
  expertStatus,
  primaryActions,
  onMessageClick,
  onMarkAsClosed,
  hideFooter = false,
  unreadCount = 0,
}: Props) => {
  const showRetainerCardFooter =
    !hideFooter &&
    Boolean(
      expert || footerParty || expertStatus || onMessageClick || onMarkAsClosed,
    );

  return (
    <StyledRetainerCard variant="outlined">
      <RetainerCardReceiptBox>
        <RetainerCardHeaderGroup>
          <RetainerCardTitle>{title}</RetainerCardTitle>
          {subtitle ? (
            <RetainerCardDescription>{subtitle}</RetainerCardDescription>
          ) : null}
        </RetainerCardHeaderGroup>
        {receiptLines.map((line) => (
          <ReceiptRow
            key={
              line.id
                ? `${line.label}-${line.id}`
                : `${line.label}-${line.value}`
            }
          >
            <ReceiptLabel>{line.label}</ReceiptLabel>
            <ReceiptValue $valueColor={line.valueColor}>
              {line.value}
            </ReceiptValue>
          </ReceiptRow>
        ))}
      </RetainerCardReceiptBox>
      {primaryActions && (
        <RetainerCardActionsRow>{primaryActions}</RetainerCardActionsRow>
      )}
      {showRetainerCardFooter && (
        <RetainerCardFooterSection>
          <RetainerCardFooterRow>
            <RetainerCardFooterPartyBlock>
              {expert && (
                <ExpertChip
                  to={`/experts/${expert.id}`}
                  onClick={(event: MouseEvent) => event.stopPropagation()}
                  aria-label={`View ${expert.name ?? 'Expert'}'s profile`}
                >
                  <RetainerCardFooterAvatar
                    person={expert}
                    nameFallback="Expert"
                  />
                  <ExpertChipName variant="caption">
                    {expert.name ?? 'Expert'}
                  </ExpertChipName>
                  {expertStatus && (
                    <ExpertChipStatus
                      variant="caption"
                      component="span"
                      $color={getExpertChipStatusColor(expertStatus.status)}
                    >
                      {expertStatus.label}
                    </ExpertChipStatus>
                  )}
                </ExpertChip>
              )}
              {footerParty && !expert && (
                <PartyChip>
                  <RetainerCardFooterAvatar
                    person={footerParty}
                    nameFallback="Attorney"
                  />
                  <ExpertChipName variant="caption">
                    {footerParty.name ?? 'Attorney'}
                  </ExpertChipName>
                  {expertStatus && (
                    <ExpertChipStatus
                      variant="caption"
                      component="span"
                      $color={getExpertChipStatusColor(expertStatus.status)}
                    >
                      {expertStatus.label}
                    </ExpertChipStatus>
                  )}
                </PartyChip>
              )}
              {!expert && !footerParty && expertStatus && (
                <RetainerStatusChip
                  label={expertStatus.label}
                  status={expertStatus.status}
                  size="small"
                />
              )}
              {onMarkAsClosed && (
                <MarkAsClosedLink
                  type="button"
                  onClick={(event: MouseEvent) => {
                    event.stopPropagation();
                    onMarkAsClosed();
                  }}
                  aria-label="Mark retainer as closed"
                >
                  Mark as closed
                </MarkAsClosedLink>
              )}
            </RetainerCardFooterPartyBlock>
            <RetainerCardFooterActions>
              {onMessageClick && (
                <MessageCountBadge
                  badgeContent={unreadCount}
                  color="error"
                  invisible={unreadCount === 0}
                >
                  <CardActionButton
                    variant="contained"
                    size="small"
                    startIcon={
                      <Icon16 as="span">
                        <Chat aria-hidden />
                      </Icon16>
                    }
                    onClick={(event: MouseEvent) => {
                      event.stopPropagation();
                      onMessageClick();
                    }}
                    $mobile={false}
                  >
                    Message
                  </CardActionButton>
                </MessageCountBadge>
              )}
            </RetainerCardFooterActions>
          </RetainerCardFooterRow>
        </RetainerCardFooterSection>
      )}
    </StyledRetainerCard>
  );
};

export default RetainerCard;
